import { motion, AnimatePresence } from "framer-motion";
import { X, Heart } from "lucide-react";
import type { EventRsvpItem } from "../lib/api/useEventRsvp";
import type { BuddyRequestItem } from "../lib/api/useEventBuddies";
import { ProfileImage } from "./ProfileImage";
import { BuddyAvatar } from "./BuddyAvatar";
import { useBackButtonClose } from "../lib/useBackButtonClose";

interface Props {
  isOpen: boolean;
  celebrate: boolean;
  onClose: () => void;
  onUnmark: () => void;
  interestedItems: EventRsvpItem[];
  isLoadingItems?: boolean;
  buddyItems: BuddyRequestItem[];
  isLoadingBuddies?: boolean;
  slug: string;
}

export function EventInterestedSheet({
  isOpen,
  celebrate: _celebrate,
  onClose,
  onUnmark,
  interestedItems,
  isLoadingItems,
  buddyItems,
  isLoadingBuddies,
  slug,
}: Props) {
  useBackButtonClose(isOpen, onClose);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[45]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />

          <div className="fixed left-0 right-0 z-[46] above-rsvp-bar flex justify-center px-4 pointer-events-none">
            <motion.div
              className="pointer-events-auto w-full max-w-sm rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-black/8 dark:border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.18)]"
              initial={{ opacity: 0, y: 52, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 28, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 420, damping: 30 }}
            >
              {/* Header — icon only, no text */}
              <div className="relative flex flex-col items-center pt-6 pb-5 px-5 border-b border-black/10 dark:border-white/10">
                <button
                  onClick={onClose}
                  className="absolute top-3.5 right-3.5 p-1.5 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>

                <motion.div
                  initial={{ scale: 0.3, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 380, damping: 16, delay: 0.08 }}
                >
                  <Heart className="w-12 h-12 fill-rose-500 text-rose-500" />
                </motion.div>
              </div>

              {/* Who's interested — always rendered to prevent layout jump */}
              <div className="px-5 py-4 border-b border-black/10 dark:border-white/10">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
                  {interestedItems.length > 0
                    ? `Who's interested · ${interestedItems.length}`
                    : "Who's interested"}
                </p>
                <div className="flex items-start gap-4 overflow-x-auto pb-1 -mx-5 px-5 min-h-[78px]">
                  {isLoadingItems && interestedItems.length === 0 ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="shrink-0 flex flex-col items-center gap-1.5">
                        <div className="h-14 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
                        </div>
                        <div className="w-10 h-2.5 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
                      </div>
                    ))
                  ) : interestedItems.length > 0 ? (
                    <>
                      {interestedItems.slice(0, 16).map((r) => (
                        <div key={r.id} className="shrink-0 flex flex-col items-center gap-1.5">
                          <div className="h-14 flex items-center justify-center">
                            <ProfileImage
                              size="md"
                              user={{ firstName: r.firstName, lastName: r.lastName, profileImageUrl: r.profileImageUrl }}
                            />
                          </div>
                          <span className="text-[11px] text-neutral-600 dark:text-neutral-400 font-medium w-16 text-center leading-tight truncate">
                            {r.firstName}
                          </span>
                        </div>
                      ))}
                      {interestedItems.length > 16 && (
                        <div className="shrink-0 flex flex-col items-center gap-1.5">
                          <div className="h-14 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
                              +{interestedItems.length - 16}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 italic self-center">
                      No one yet — be the first!
                    </p>
                  )}
                </div>
              </div>

              {/* Kiki — always rendered */}
              <div className="px-5 py-4 border-b border-black/10 dark:border-white/10">
                <div className="flex items-center gap-1.5 mb-1">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                    Let's have a kiki
                  </p>
                  <a
                    href={`/events/${slug}`}
                    className="ml-auto text-xs font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    onClick={onClose}
                  >
                    + Find others
                  </a>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                  Connect with others going to this event and go together.
                </p>
                <div className="min-h-[56px] flex items-center">
                  {isLoadingBuddies && buddyItems.length === 0 ? (
                    <div className="flex gap-4 -mx-5 px-5">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="shrink-0 flex flex-col items-center gap-1.5">
                          <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
                          <div className="w-10 h-2.5 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  ) : buddyItems.length > 0 ? (
                    <div className="flex items-start gap-4 overflow-x-auto pb-1 -mx-5 px-5 w-full">
                      {buddyItems.slice(0, 8).map((b) => (
                        <BuddyAvatar key={b.id} buddy={b} onClick={onClose} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 italic">
                      No one yet — be the first!
                    </p>
                  )}
                </div>
              </div>

              {/* Remove interest */}
              <div className="px-5 py-3 flex justify-center">
                <button
                  type="button"
                  onClick={onUnmark}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-400 dark:text-neutral-500 hover:text-rose-500 dark:hover:text-rose-400 transition-colors"
                >
                  <Heart className="w-3 h-3" />
                  Remove interest
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
